import {
  AppstoreAddOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  EnterOutlined,
  LogoutOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Layout,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  clearControlToken,
  controlLogin,
  controlRequest,
  enterManagedSystem,
  getControlToken,
} from './api';
import type { ControlAuditLog, ControlOverview, ControlSecurity, ControlSystem, ControlSystemStatus } from './types';

const emptyOverview: ControlOverview = { total: 0, active: 0, suspended: 0, provisioning: 0, miniAppBindings: 0 };
const statusMeta: Record<ControlSystemStatus, { color: string; label: string }> = {
  active: { color: 'success', label: '运行中' },
  suspended: { color: 'warning', label: '已暂停' },
  provisioning: { color: 'processing', label: '初始化中' },
  failed: { color: 'error', label: '初始化失败' },
};
const actionNames: Record<string, string> = {
  'control.login': '登录总管理层',
  'system.create': '创建子系统',
  'system.update': '修改子系统',
  'system.delete': '删除子系统',
  'system.impersonate': '代管进入子系统',
  'control.security.update': '更新总控安全设置',
};

function askSecondaryPassword(title: string, content: string, danger = false): Promise<string | null> {
  let password = '';
  return new Promise(resolve => {
    Modal.confirm({
      title,
      content: <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text type="secondary">{content}</Typography.Text>
        <Input.Password autoFocus placeholder="请输入总控二级密码" onChange={event => { password = event.target.value; }} />
      </Space>,
      okText: '验证并继续',
      okButtonProps: danger ? { danger: true } : {},
      cancelText: '取消',
      onOk: () => {
        if (!password) {
          message.error('请输入二级密码');
          return Promise.reject(new Error('请输入二级密码'));
        }
        resolve(password);
      },
      onCancel: () => resolve(null),
    });
  });
}

function formatDate(value: string | number | Date | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('zh-CN', { hour12: false });
}

function ControlLogin({ onSuccess }: { onSuccess: () => void }) {
  const [loading, setLoading] = useState(false);
  const submit = async (values: { key: string }) => {
    setLoading(true);
    try {
      await controlLogin(values.key);
      message.success('总管理登录成功');
      onSuccess();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  };
  return <div className="login-shell control-login-shell">
    <div className="login-orb orb-one" /><div className="login-orb orb-two" />
    <section className="login-story">
      <div className="brand-mark large"><span>图</span></div>
      <Typography.Title>一个平台，管理所有<br />独立小程序系统。</Typography.Title>
      <Typography.Paragraph>平台配置、业务数据、文件资源与管理员权限完整隔离</Typography.Paragraph>
      <div className="login-chips"><Tag>独立数据库</Tag><Tag>独立服务配置</Tag><Tag>操作审计</Tag></div>
    </section>
    <section className="login-panel">
      <div className="login-card">
        <div className="mobile-logo"><div className="brand-mark"><span>图</span></div><div><strong>壁纸平台总控</strong><small>Wallpaper Platform Master Control</small></div></div>
        <Typography.Title level={2}>总管理层</Typography.Title>
        <Typography.Paragraph type="secondary">使用总管理密钥登录，不与任何子系统账号共用令牌。</Typography.Paragraph>
        <Form layout="vertical" onFinish={submit} requiredMark={false} size="large">
          <Form.Item name="key" label="总管理密钥" rules={[{ required: true, message: '请输入总管理密钥' }]}>
            <Input.Password autoFocus autoComplete="current-password" placeholder="输入总管理密钥" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>进入平台总控</Button>
        </Form>
        <div className="login-security">代管进入子系统的令牌有效期为 30 分钟，所有操作均记录审计日志</div>
        <Button type="link" block href="/">返回业务后台</Button>
      </div>
    </section>
  </div>;
}

export default function ControlApp() {
  const [authenticated, setAuthenticated] = useState(Boolean(getControlToken()));
  const [checking, setChecking] = useState(Boolean(getControlToken()));
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<ControlOverview>(emptyOverview);
  const [systems, setSystems] = useState<ControlSystem[]>([]);
  const [audits, setAudits] = useState<ControlAuditLog[]>([]);
  const [security, setSecurity] = useState<ControlSecurity>({ displayName: '总管理员', hasSecondaryPassword: false, hasIndependentControlKey: false });
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ControlSystem | null>(null);
  const [saving, setSaving] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [securitySaving, setSecuritySaving] = useState(false);
  const [createForm] = Form.useForm<{ name: string; systemId: string; adminDisplayName: string; adminKey: string; confirmKey: string }>();
  const [editForm] = Form.useForm<{ name: string; adminDisplayName: string; adminKey?: string; confirmKey?: string }>();
  const [securityForm] = Form.useForm<{ displayName: string; currentSecondaryPassword?: string; newSecondaryPassword?: string; confirmSecondaryPassword?: string; newControlAdminKey?: string; confirmControlAdminKey?: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewResult, systemResult, auditResult, securityResult] = await Promise.all([
        controlRequest<ControlOverview>('/api/control/overview'),
        controlRequest<ControlSystem[]>('/api/control/systems'),
        controlRequest<ControlAuditLog[]>('/api/control/audit-logs?limit=100'),
        controlRequest<ControlSecurity>('/api/control/security'),
      ]);
      setOverview(overviewResult.data);
      setSystems(systemResult.data || []);
      setAudits(auditResult.data || []);
      setSecurity(securityResult.data);
      setAuthenticated(true);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setAuthenticated(false);
      else message.error((error as Error).message);
    } finally {
      setLoading(false);
      setChecking(false);
    }
  }, []);

  useEffect(() => { if (getControlToken()) void load(); else setChecking(false); }, [load]);

  const createSystem = async () => {
    const values = await createForm.validateFields();
    if (!security.hasSecondaryPassword) {
      message.warning('请先创建总控二级密码');
      securityForm.setFieldsValue({ displayName: security.displayName });
      setSecurityOpen(true);
      return;
    }
    const secondaryPassword = await askSecondaryPassword('验证后创建子系统', `即将创建“${values.name}”及独立数据库。`);
    if (!secondaryPassword) return;
    setCreating(true);
    try {
      await controlRequest<ControlSystem>('/api/control/systems', {
        method: 'POST',
        data: {
          name: values.name,
          systemId: values.systemId,
          adminDisplayName: values.adminDisplayName,
          adminKey: values.adminKey,
          secondaryPassword,
        },
      });
      message.success('子系统创建完成，可以进入后台配置小程序');
      setCreateOpen(false);
      createForm.resetFields();
      await load();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const saveName = async () => {
    if (!editing) return;
    const values = await editForm.validateFields();
    if (!security.hasSecondaryPassword) {
      message.warning('请先创建总控二级密码');
      openSecurity();
      return;
    }
    const secondaryPassword = await askSecondaryPassword('验证后保存子系统信息', values.adminKey ? '本次将同时重置主管理员独立管理密钥。' : '本次将修改子系统或主管理员显示信息。');
    if (!secondaryPassword) return;
    setSaving(true);
    try {
      await controlRequest(`/api/control/systems/${encodeURIComponent(editing.systemId)}`, {
        method: 'PUT',
        data: {
          name: values.name,
          adminDisplayName: values.adminDisplayName,
          ...(values.adminKey ? { adminKey: values.adminKey } : {}),
          secondaryPassword,
        },
      });
      message.success('子系统信息已更新');
      setEditing(null);
      editForm.resetFields();
      await load();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = (system: ControlSystem) => {
    if (!security.hasSecondaryPassword) {
      message.warning('请先创建总控二级密码');
      openSecurity();
      return;
    }
    const next = system.status === 'active' ? 'suspended' : 'active';
    void (async () => {
      const secondaryPassword = await askSecondaryPassword(
        next === 'suspended' ? `暂停“${system.name}”？` : `恢复“${system.name}”？`,
        next === 'suspended' ? '暂停后小程序和后台账号将立即停止访问，数据不会删除。' : '恢复后小程序和后台账号将重新允许访问。',
        next === 'suspended',
      );
      if (!secondaryPassword) return;
      try {
        await controlRequest(`/api/control/systems/${encodeURIComponent(system.systemId)}`, { method: 'PUT', data: { status: next, secondaryPassword } });
        message.success(next === 'suspended' ? '子系统已暂停' : '子系统已恢复');
        await load();
      } catch (error) { message.error((error as Error).message); }
    })();
  };

  const enterSystem = async (system: ControlSystem) => {
    try {
      if (!security.hasSecondaryPassword) {
        message.warning('请先创建总控二级密码');
        openSecurity();
        return;
      }
      const secondaryPassword = await askSecondaryPassword('验证后代管进入', `将以主管理员权限进入“${system.name}”，令牌有效期为 30 分钟。`);
      if (!secondaryPassword) return;
      const result = await controlRequest<{ adminToken: string }>(`/api/control/systems/${encodeURIComponent(system.systemId)}/impersonate`, { method: 'POST', data: { secondaryPassword } });
      message.success(`正在进入“${system.name}”`);
      enterManagedSystem(result.data.adminToken, system.systemId);
    } catch (error) {
      message.error((error as Error).message);
    }
  };

  const deleteSystem = (system: ControlSystem) => {
    if (!security.hasSecondaryPassword) {
      message.warning('请先创建总控二级密码');
      openSecurity();
      return;
    }
    void (async () => {
      const secondaryPassword = await askSecondaryPassword(
        `永久删除“${system.name}”？`,
        '将永久删除该子系统数据库、小程序绑定和服务器服务配置。外部对象存储中的文件不会自动删除。此操作不可撤销。',
        true,
      );
      if (!secondaryPassword) return;
      try {
        await controlRequest(`/api/control/systems/${encodeURIComponent(system.systemId)}`, { method: 'DELETE', data: { secondaryPassword } });
        message.success('子系统已删除；外部对象存储文件仍保留');
        await load();
      } catch (error) { message.error((error as Error).message); }
    })();
  };

  const openSecurity = () => {
    securityForm.setFieldsValue({
      displayName: security.displayName,
      currentSecondaryPassword: '',
      newSecondaryPassword: '',
      confirmSecondaryPassword: '',
      newControlAdminKey: '',
      confirmControlAdminKey: '',
    });
    setSecurityOpen(true);
  };

  const saveSecurity = async () => {
    const values = await securityForm.validateFields();
    setSecuritySaving(true);
    try {
      const result = await controlRequest<ControlSecurity>('/api/control/security', {
        method: 'PUT',
        data: {
          displayName: values.displayName,
          ...(values.currentSecondaryPassword ? { currentSecondaryPassword: values.currentSecondaryPassword } : {}),
          ...(values.newSecondaryPassword ? { newSecondaryPassword: values.newSecondaryPassword } : {}),
          ...(values.newControlAdminKey ? { newControlAdminKey: values.newControlAdminKey } : {}),
        },
      });
      setSecurity(result.data);
      setSecurityOpen(false);
      securityForm.resetFields();
      message.success(result.data.hasSecondaryPassword ? '总控安全设置已保存' : '总控资料已保存');
      await load();
    } catch (error) { message.error((error as Error).message); }
    finally { setSecuritySaving(false); }
  };

  const columns = [
    {
      title: '子系统', key: 'system', width: 240,
      render: (_: unknown, item: ControlSystem) => <div className="control-system-name"><div className="control-system-title"><strong>{item.name}</strong><Typography.Text className="control-system-id" type="secondary" copyable>{item.systemId}</Typography.Text></div><Typography.Text type="secondary">主管理员：{item.adminDisplayName || '主管理员'}</Typography.Text></div>,
    },
    { title: '状态', dataIndex: 'status', width: 110, render: (status: ControlSystemStatus) => <Tag color={statusMeta[status].color}>{statusMeta[status].label}</Tag> },
    { title: '数据库', dataIndex: 'databaseName', width: 200, render: (value: string) => <Typography.Text code>{value}</Typography.Text> },
    {
      title: '业务用量', key: 'usage', width: 250,
      render: (_: unknown, item: ControlSystem) => <Space size={[4, 4]} wrap>
        <Tag>壁纸 {item.usage?.wallpapers || 0}</Tag><Tag>用户 {item.usage?.users || 0}</Tag><Tag>账号 {item.usage?.staff || 0}</Tag><Tag>小程序 {item.usage?.miniApps || 0}</Tag>
      </Space>,
    },
    { title: '创建时间', dataIndex: 'createdAt', width: 180, render: formatDate },
    {
      title: '操作', key: 'actions', fixed: 'right' as const, width: 360,
      render: (_: unknown, item: ControlSystem) => <Space wrap>
        <Button size="small" type="primary" icon={<EnterOutlined />} disabled={item.status !== 'active'} onClick={() => void enterSystem(item)}>进入</Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(item); editForm.setFieldsValue({ name: item.name, adminDisplayName: item.adminDisplayName, adminKey: '', confirmKey: '' }); }}>编辑</Button>
        {!item.isDefault && ['active', 'suspended'].includes(item.status) ? <Button size="small" danger={item.status === 'active'} icon={item.status === 'active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={() => changeStatus(item)}>{item.status === 'active' ? '暂停' : '恢复'}</Button> : null}
        {!item.isDefault ? <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteSystem(item)}>删除</Button> : null}
      </Space>,
    },
  ];

  if (checking) return <div className="full-spin"><Spin size="large" tip="正在验证总管理登录状态" /></div>;
  if (!authenticated) return <ControlLogin onSuccess={() => { setAuthenticated(true); void load(); }} />;

  return <Layout className="control-layout">
    <header className="control-header">
      <div className="control-brand"><div className="brand-mark"><span>图</span></div><div><strong>壁纸平台总控</strong><small>Wallpaper Platform Master Control</small></div></div>
      <Space>
        <Typography.Text style={{ color: '#d7e0e8' }}>{security.displayName}</Typography.Text>
        <Button icon={<SettingOutlined />} onClick={openSecurity}>安全设置</Button>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>刷新</Button>
        <Button icon={<LogoutOutlined />} onClick={() => { clearControlToken(); setAuthenticated(false); }}>退出总控</Button>
      </Space>
    </header>
    <Layout.Content className="control-content">
      <div className="control-title-row">
        <div><Typography.Title level={2}>子系统管理</Typography.Title><Typography.Paragraph type="secondary">每个子系统拥有独立数据库、管理员、小程序、用户、内容和服务配置。</Typography.Paragraph></div>
        <Button type="primary" size="large" icon={<AppstoreAddOutlined />} onClick={() => setCreateOpen(true)}>创建子系统</Button>
      </div>
      {!security.hasSecondaryPassword ? <Alert type="warning" showIcon message="尚未创建总控二级密码" description="创建、编辑、暂停、代管进入和删除子系统前必须验证二级密码。请先完成安全设置。" action={<Button type="primary" onClick={openSecurity}>立即设置</Button>} style={{ marginBottom: 16 }} /> : null}
      {!security.hasIndependentControlKey ? <Alert type="info" showIcon message="建议设置独立总管理登录密钥" description="设置后，总控登录密钥将与官方子系统主管理员密钥彻底分离；在此之前不允许重置官方系统管理密钥。" action={<Button onClick={openSecurity}>前往设置</Button>} style={{ marginBottom: 16 }} /> : null}
      <Row gutter={[16, 16]} className="control-stat-row">
        <Col xs={12} lg={6}><Card><Statistic title="全部子系统" value={overview.total} prefix={<DatabaseOutlined />} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="运行中" value={overview.active} valueStyle={{ color: '#16a34a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="已暂停" value={overview.suspended} valueStyle={{ color: '#d97706' }} prefix={<PauseCircleOutlined />} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="小程序绑定" value={overview.miniAppBindings} prefix={<SafetyCertificateOutlined />} /></Card></Col>
      </Row>
      <Card className="control-system-card" title="子系统列表" extra={<Typography.Text type="secondary">官方系统不可暂停</Typography.Text>}>
        <Table rowKey="systemId" loading={loading} columns={columns} dataSource={systems} scroll={{ x: 1240 }} pagination={false} expandable={{
          expandedRowRender: item => item.failureMessage ? <Alert type="error" showIcon message="初始化失败" description={item.failureMessage} /> : <Descriptions size="small" column={2} items={[
            { key: 'id', label: '系统标识', children: item.systemId },
            { key: 'database', label: '独立数据库', children: item.databaseName },
            { key: 'updated', label: '最近更新', children: formatDate(item.updatedAt) },
            { key: 'default', label: '系统类型', children: item.isDefault ? '默认官方系统' : '独立业务子系统' },
            { key: 'admin', label: '主管理员显示名称', children: item.adminDisplayName || '主管理员' },
            { key: 'brand', label: '后台品牌', children: `${item.adminBrand?.title || '潮图后台'}${item.adminBrand?.subtitle ? ` / ${item.adminBrand.subtitle}` : ''}` },
          ]} />,
        }} />
      </Card>
      <Card className="control-audit-card" title="最近操作审计">
        <Table rowKey="_id" size="small" dataSource={audits} pagination={{ pageSize: 10, hideOnSinglePage: true }} columns={[
          { title: '时间', dataIndex: 'createdAt', width: 180, render: formatDate },
          { title: '操作', dataIndex: 'action', width: 180, render: (value: string) => actionNames[value] || value },
          { title: '子系统', dataIndex: 'systemId', width: 160, render: (value: string) => <Typography.Text code>{value}</Typography.Text> },
          { title: '操作者', dataIndex: 'operatorId', width: 160 },
          { title: '详情', dataIndex: 'details', ellipsis: true, render: (value: Record<string, unknown>) => <Typography.Text type="secondary">{Object.entries(value || {}).map(([key, item]) => `${key}: ${String(item)}`).join('；') || '—'}</Typography.Text> },
        ]} />
      </Card>
    </Layout.Content>

    <Modal width={620} open={createOpen} title="创建独立子系统" okText="创建并初始化" cancelText="取消" confirmLoading={creating} onOk={() => void createSystem()} onCancel={() => setCreateOpen(false)}>
      <Alert type="info" showIcon message="仅复制初始设置" description="会复制官方系统的基础运营设置和内容类型；不会复制壁纸、用户、订单、创作者、小程序实例或日志。创建后请进入该子系统配置自己的小程序和服务凭据。" style={{ marginBottom: 18 }} />
      <Form form={createForm} layout="vertical" requiredMark={false} initialValues={{ adminDisplayName: '主管理员' }}>
        <Row gutter={14}>
          <Col span={12}><Form.Item name="name" label="子系统名称" rules={[{ required: true, message: '请输入名称' }]}><Input maxLength={80} placeholder="例如：品牌 A 壁纸" /></Form.Item></Col>
          <Col span={12}><Form.Item name="systemId" label="系统标识" extra="创建后不可修改" rules={[{ required: true, message: '请输入系统标识' }, { pattern: /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/, message: '使用 3-40 位小写字母、数字或短横线' }]}><Input maxLength={40} placeholder="例如：brand-a" /></Form.Item></Col>
        </Row>
        <Form.Item name="adminDisplayName" label="主管理员显示名称" rules={[{ required: true, message: '请输入显示名称' }]}><Input maxLength={40} /></Form.Item>
        <Row gutter={14}>
          <Col span={12}><Form.Item name="adminKey" label="独立管理密钥" rules={[{ required: true, message: '请输入管理密钥' }, { min: 8, message: '至少 8 位' }, { max: 128 }]}><Input.Password autoComplete="new-password" /></Form.Item></Col>
          <Col span={12}><Form.Item name="confirmKey" label="确认管理密钥" dependencies={['adminKey']} rules={[{ required: true, message: '请再次输入管理密钥' }, ({ getFieldValue }) => ({ validator(_, value) { return !value || value === getFieldValue('adminKey') ? Promise.resolve() : Promise.reject(new Error('两次输入的密钥不一致')); } })]}><Input.Password autoComplete="new-password" /></Form.Item></Col>
        </Row>
      </Form>
    </Modal>

    <Modal width={620} open={Boolean(editing)} title="编辑子系统信息" okText="验证并保存" cancelText="取消" confirmLoading={saving} onOk={() => void saveName()} onCancel={() => { setEditing(null); editForm.resetFields(); }}>
      <Alert type="info" showIcon message="主管理员资料重置" description="独立管理密钥留空表示保持不变；填写后将直接重置该子系统主管理员登录密钥。保存时还需验证总控二级密码。" style={{ marginBottom: 18 }} />
      <Form form={editForm} layout="vertical" requiredMark={false}>
        <Form.Item name="name" label="子系统名称" rules={[{ required: true, message: '请输入名称' }]}><Input maxLength={80} /></Form.Item>
        <Form.Item name="adminDisplayName" label="主管理员显示名称" rules={[{ required: true, message: '请输入主管理员显示名称' }]}><Input maxLength={40} /></Form.Item>
        <Row gutter={14}>
          <Col span={12}><Form.Item name="adminKey" label="重置独立管理密钥" rules={[{ min: 8, message: '至少 8 位' }, { max: 128 }]}><Input.Password autoComplete="new-password" placeholder="留空则不修改" /></Form.Item></Col>
          <Col span={12}><Form.Item name="confirmKey" label="确认新管理密钥" dependencies={['adminKey']} rules={[({ getFieldValue }) => ({ validator(_, value) { const key = getFieldValue('adminKey'); return (!key && !value) || value === key ? Promise.resolve() : Promise.reject(new Error('两次输入的密钥不一致')); } })]}><Input.Password autoComplete="new-password" placeholder="再次输入新密钥" /></Form.Item></Col>
        </Row>
      </Form>
    </Modal>

    <Modal width={560} open={securityOpen} title="总控安全设置" okText="保存安全设置" cancelText="取消" confirmLoading={securitySaving} onOk={() => void saveSecurity()} onCancel={() => { setSecurityOpen(false); securityForm.resetFields(); }}>
      <Alert type={security.hasSecondaryPassword ? 'info' : 'warning'} showIcon message={security.hasSecondaryPassword ? '敏感操作受二级密码保护' : '首次使用必须创建二级密码'} description="二级密码独立于总管理登录密钥，用于创建、编辑、暂停、代管进入和删除子系统。请勿与总管理密钥设置为相同内容。" style={{ marginBottom: 18 }} />
      <Form form={securityForm} layout="vertical" requiredMark={false}>
        <Form.Item name="displayName" label="总管理员显示名称" rules={[{ required: true, message: '请输入显示名称' }]}><Input maxLength={40} /></Form.Item>
        {security.hasSecondaryPassword ? <Form.Item name="currentSecondaryPassword" label="当前二级密码" rules={[{ required: true, message: '请输入当前二级密码' }]}><Input.Password autoComplete="current-password" /></Form.Item> : null}
        <Form.Item name="newSecondaryPassword" label={security.hasSecondaryPassword ? '新二级密码（留空不修改）' : '创建二级密码'} rules={[
          { required: !security.hasSecondaryPassword, message: '请创建二级密码' },
          { min: 8, message: '至少 8 位' },
          { max: 128 },
        ]}><Input.Password autoComplete="new-password" /></Form.Item>
        <Form.Item name="confirmSecondaryPassword" label="确认新二级密码" dependencies={['newSecondaryPassword']} rules={[({ getFieldValue }) => ({ validator(_, value) { const password = getFieldValue('newSecondaryPassword'); return (!password && !value) || value === password ? Promise.resolve() : Promise.reject(new Error('两次输入的二级密码不一致')); } })]}><Input.Password autoComplete="new-password" /></Form.Item>
        <Form.Item name="newControlAdminKey" label={security.hasIndependentControlKey ? '重置独立总管理登录密钥（留空不修改）' : '独立总管理登录密钥（推荐设置）'} extra="设置后，进入总控页面将使用这把密钥，不再跟随官方子系统主管理员密钥。" rules={[{ min: 8, message: '至少 8 位' }, { max: 128 }]}><Input.Password autoComplete="new-password" /></Form.Item>
        <Form.Item name="confirmControlAdminKey" label="确认独立总管理登录密钥" dependencies={['newControlAdminKey']} rules={[({ getFieldValue }) => ({ validator(_, value) { const password = getFieldValue('newControlAdminKey'); return (!password && !value) || value === password ? Promise.resolve() : Promise.reject(new Error('两次输入的总管理密钥不一致')); } })]}><Input.Password autoComplete="new-password" /></Form.Item>
      </Form>
    </Modal>
  </Layout>;
}
